//
// Copyright 2024 DXOS.org
//

import * as Schema from 'effect/Schema';

import { BaseError } from '@dxos/errors';
import { invariant } from '@dxos/invariant';
import { SpaceId } from '@dxos/keys';

/**
 * HTTP header sent on every Edge request to classify traffic for metering.
 */
export const EDGE_CLIENT_TAG_HEADER = 'X-DXOS-Client-Tag';

/**
 * HTTP header sent on every Edge request to provide a BYOK (Bring Your Own Key) for AI services.
 */
export const BYOK_HEADER = 'X-BYOK';

// TODO(burdon): Rename EdgerRouterEndpoint.
// If we would rename it, we need to be careful to not break composer production.
export enum EdgeService {
  SUBDUCTION_REPLICATOR = 'subduction-replicator',
  /**
   * Control feed replicator (hypercore append only logs) for the space.
   */
  // TODO(mykola): Remove once we migrate to keyhive for access control.
  FEED_REPLICATOR = 'feed-replicator',
  /**
   * Feed replicator (ordered data list) for the space.
   */
  // TODO(mykola): Rename to FEED_REPLICATOR when we migrate to keyhive for access control.
  QUEUE_REPLICATOR = 'queue-replicator',
  SWARM = 'swarm',
  SIGNAL = 'signal',
  STATUS = 'status',
  /**
   * User-to-user notices held for an identity until acknowledged (e.g., space invitation notices).
   */
  INBOX = 'inbox',
}

export type EdgeSuccess<T> = {
  success: true;
  data: T;
};

const _SerializedError = Schema.Struct({
  name: Schema.optional(Schema.String),
  message: Schema.optional(Schema.String),
  context: Schema.optional(Schema.Record(Schema.String, Schema.Any)),
  stack: Schema.optional(Schema.String),
  cause: Schema.optional(Schema.suspend(() => SerializedError)),
});
export interface SerializedError extends Schema.Schema.Type<typeof _SerializedError> {}
export const SerializedError: Schema.Codec<SerializedError, SerializedError, never> = _SerializedError;

export type EdgeErrorData = { type: string } & Record<string, any>;

/**
 * This is the shape of the error response from the Edge service,
 * when the error is gracefully handled, the Response will be an object with this shape and have status code 200.
 */
export type EdgeFailure = {
  /**
   * Branded Type.
   */
  success: false;
  /**
   * An explanation of why the call failed. Used mostly for logging and monitoring.
   */
  message: string;
  /**
   * Cause Error captured on the EDGE service to aid debugging on the client.
   */
  error?: SerializedError;
  /**
   * Information that can be used to retry the request such that it will succeed, for example:
   * 1. { type: 'auth_required', challenge: string }
   *    Requires retrying the request with challenge signature included.
   * 2. { type: 'user_confirmation_required', dialog: { title: string, message: string, confirmation_payload: string } }
   *    Requires showing a confirmation dialog to a user and retrying the request with confirmation_payload included
   *    if the user confirms.
   * When data is returned simply retrying the request won't have any effect.
   * EdgeHttpClient should parse well-known data into Error types and throw.
   */
  data?: EdgeErrorData;
};

/**
 * Represents a body response from the Edge service.
 */
export type EdgeEnvelope<T> = EdgeSuccess<T> | EdgeFailure;

/**
 * Use this to create a response from the Edge service.
 */
export const EdgeResponse = Object.freeze({
  success: <T>(data: T, status: number = 200): Response => {
    invariant(status >= 200 && status < 300, 'Status code must be in the 2xx range');
    const headers = new Headers({ 'Content-Type': 'application/json' });
    return new Response(
      JSON.stringify({
        success: true,
        data,
      } satisfies EdgeSuccess<T>),
      {
        status,
        headers,
      },
    );
  },
  failure: ({
    message,
    error,
    errorEncoded,
    data,
    shouldRetryAfter,
    status = 500,
  }: {
    /**
     * An explanation of why the call failed. Used mostly for logging and monitoring.
     */
    message: string;
    /**
     * Error that caused the failure.
     * Useful for debugging.
     *
     * Use only one of the fields `error` or `errorEncoded`.s
     */
    error?: Error;
    /**
     * Encoded Error that caused the failure.
     * Useful for debugging.
     *
     * Use only one of the fields `error` or `errorEncoded`.
     */
    errorEncoded?: SerializedError;

    /**
     * Information that can be used to retry the request such that it will succeed, for example:
     * 1. { type: 'auth_required', challenge: string }
     *    Requires retrying the request with challenge signature included.
     * 2. { type: 'user_confirmation_required', dialog: { title: string, message: string, confirmation_payload: string } }
     *    Requires showing a confirmation dialog to a user and retrying the request with confirmation_payload included
     *    if the user confirms.
     * When data is returned simply retrying the request won't have any effect.
     * EdgeHttpClient should parse well-known data into Error types and throw.
     */
    data?: EdgeErrorData;
    /**
     * If provided, this request will be marked as retryable and the client will wait for the specified number of milliseconds before retrying.
     * If not provided, the client will not retry the request.
     */
    shouldRetryAfter?: number;

    /**
     * Status code of the response.
     * @default 500
     */
    status?: number;
  }): Response => {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    if (shouldRetryAfter) {
      headers.set('Retry-After', String(shouldRetryAfter));
    }

    return new Response(
      JSON.stringify({
        success: false,
        message,
        data,
        error: error ? ErrorCodec.encode(error) : errorEncoded,
      } satisfies EdgeFailure),
      {
        status,
        headers,
      },
    );
  },
});

export type GetNotarizationResponseBody = {
  awaitingNotarization: { credentials: string[] };
};

export type ExecuteWorkflowResponseBody = {
  success: boolean;
  reason?: string;
  output?: any;
};

export type PostNotarizationRequestBody = {
  credentials: string[];
};

export type JoinSpaceRequest = {
  invitationId: string;
  identityKey: string;
  /**
   * Base64 encoded signed challenge.
   * Used to verify the IdentityKey in case of `invitation.authMethod === Invitation_AuthMethod.KNOWN_PUBLIC_KEY`.
   */
  signature?: string;
};

export type JoinSpaceResponseBody = {
  spaceMemberCredential: string;
  spaceGenesisFeedKey: string;
};

export type RecoverIdentitySignature =
  | string
  // This is the format of the signature from the WebAuthn authenticator.
  | {
      signature: string;
      clientDataJson: string;
      authenticatorData: string;
    };

export type RecoverIdentityRequest = {
  deviceKey: string;
  controlFeedKey: string;
  lookupKey?: string;
  signature?: RecoverIdentitySignature;
  token?: string;
  /**
   * One-time proof minted by kms-service after a successful OAuth recovery flow.
   * When provided, db-service redeems the proof to obtain the bound identityKey directly
   * and `lookupKey`/`signature` are not required.
   */
  recoveryProof?: string;
};

export type RecoverIdentityResponseBody = {
  identityKey: string;
  haloSpaceKey: string;
  genesisFeedKey: string;
  deviceAuthCredential: string;
  /**
   * Automerge URL of the halo space root, when EDGE knows one. A recovering device has only
   * `haloSpaceKey` to rebuild the space from, so without this it cannot find the document the
   * credential chain lives in. Optional while EDGE has not been taught to record it.
   */
  haloSpaceRootUrl?: string;
};

export type CreateAgentRequestBody = {
  /** Owner identity DID (`did:halo:…`). */
  identityDid: string;
  haloSpaceId: SpaceId;
  haloSpaceKey: string;
};

export type CreateAgentResponseBody = {
  deviceKey: string;
  feedKey: string;
};

export type GetAgentStatusResponseBody = {
  agent: {
    deviceKey?: string;
    status: EdgeAgentStatus;
  };
};

export type UploadFunctionRequest = {
  name?: string;
  version: string;
  /** Owner identity DID (`did:halo:…`). The Edge requires it to equal the authenticated presenter DID. */
  ownerUri: string;
  entryPoint: string;
  assets: Record<string, Uint8Array>;
  /**
   * Runtime in Edge that will be used to run the function.
   * Runtime cannot be changed once the function was deployed.
   * @default Runtime.WORKERS_FOR_PLATFORMS
   */
  runtime?: FunctionRuntimeKind;
};

/**
 * Note: Do not change the values of these enums, this values are stored in the FunctionVersions database.
 */
export const FunctionRuntimeKind = Schema.Enum({
  // https://developers.cloudflare.com/cloudflare-for-platforms/workers-for-platforms/
  WORKERS_FOR_PLATFORMS: 'WORKERS_FOR_PLATFORMS',
  // https://developers.cloudflare.com/workers/runtime-apis/bindings/worker-loader/
  WORKER_LOADER: 'WORKER_LOADER',
});
export type FunctionRuntimeKind = Schema.Schema.Type<typeof FunctionRuntimeKind>;

export type UploadFunctionResponseBody = {
  functionId: string;
  version: string;
  meta: {
    key?: string;
    name?: string;
    description?: string;
    /**
     * JSON Schema for the input of the function.
     */
    inputSchema?: object;
    /**
     * JSON Schema for the output of the function.
     */
    outputSchema?: object;
  };
};

export enum EdgeAgentStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  NOT_FOUND = 'not_found',
}

export type EdgeAuthChallenge = {
  type: 'auth_challenge';
  challenge: string;
};

export enum OAuthProvider {
  ATLASSIAN = 'atlassian',
  ATPROTO = 'atproto',
  CLOUDFLARE = 'cloudflare',
  DISCORD = 'discord',
  GITHUB = 'github',
  GOOGLE = 'google',
  LINEAR = 'linear',
  /** An arbitrary MCP server signed in to with MCP authorization; the request's `resource` names it. */
  MCP = 'mcp',
  NOTION = 'notion',
  SLACK = 'slack',
  TRELLO = 'trello',
}

/** atproto OAuth scopes for the Atmosphere integration and account-recovery flows. */
export const ATPROTO_OAUTH_SCOPES = ['atproto', 'transition:generic', 'transition:email'] as const;

/**
 * `AccessToken.source` for the Atmosphere connection — the atproto account bound to a DXOS identity.
 * atproto accounts are portable (both PDS and handle can change), so this is not a hostname. Every
 * subsystem that looks up the account's credential matches on it, hence its home beside
 * {@link OAuthProvider}. The connector that operates the connection is identified separately, by
 * {@link OAuthProvider.ATPROTO} as its `Connector.id`.
 */
export const ATMOSPHERE_SOURCE = 'atproto.local';

export const InitiateOAuthFlowRequestSchema = Schema.Struct({
  provider: Schema.Enum(OAuthProvider),
  spaceId: Schema.String.pipe(Schema.refine(SpaceId.isValid)), // TODO(burdon): Use SpaceId.
  accessTokenId: Schema.String,
  scopes: Schema.mutable(Schema.Array(Schema.String)),
  // Whose identity the grant acts as: the authorizing user (default) or DXOS's registered app. For
  // GitHub an app grant is an App installation rather than an OAuth grant.
  kind: Schema.optional(Schema.Literals(['user', 'app'])),
  // The MCP server URL a `provider: 'mcp'` grant is for. EDGE discovers its authorization server
  // from it, and the token is issued for that server alone (RFC 8707 resource indicator).
  resource: Schema.optional(Schema.String),
  // Set to true if we don't want periodic token refreshes in background, for cases like account connect
  noRefresh: Schema.optional(Schema.Boolean),
  // Provider-specific (user handle or did for atproto) hint for auth server resolution
  loginHint: Schema.optional(Schema.String),
  // Return a 302 redirect to composer://oauth/callback instead of HTML.
  // Required for ASWebAuthenticationSession (iOS) which blocks JavaScript redirects.
  nativeAppRedirect: Schema.optional(Schema.Boolean),
  // OAuth-based account recovery: when purpose === 'register', kms-service writes a
  // recovery binding for `identityKey` after the OAuth flow completes; when 'recovery',
  // kms-service mints a one-time `recoveryProof` the client forwards to db-service.
  registerRecovery: Schema.optional(Schema.Boolean),
  identityKey: Schema.optional(Schema.String),
  purpose: Schema.optional(Schema.Literals(['register', 'recovery'])),
});
export type InitiateOAuthFlowRequest = Schema.Schema.Type<typeof InitiateOAuthFlowRequestSchema>;

export type InitiateOAuthFlowResponse = {
  authUrl: string;
};

export type OAuthFlowResult =
  | { success: true; accessToken: string; accessTokenId: string; recoveryProof?: string }
  | { success: false; reason: string };

/**
 * Stand-in that EDGE returns (and clients persist on the `AccessToken` object) in place of a real
 * token for providers whose credentials EDGE custodies. The token itself is never replicated into
 * a space; holders of this value fetch the live one from `/oauth/token` per use.
 */
export const MANAGED_ACCESS_TOKEN = 'dxos:managed-access-token';

/** Whether a stored credential is a {@link MANAGED_ACCESS_TOKEN} placeholder rather than a usable token. */
export const isManagedAccessToken = (token: string | undefined): boolean => token === MANAGED_ACCESS_TOKEN;

/** Request for the live access token behind a {@link MANAGED_ACCESS_TOKEN} placeholder. */
export type GetAccessTokenRequest = {
  spaceId: SpaceId;
  /** Id of the `AccessToken` object the grant was registered against. */
  accessTokenId: string;
};

export type GetAccessTokenResponseBody = {
  accessToken: string;
  /** Server-tracked expiry, for client-side caching only. */
  expiresAtMillis: number;
};

/**
 * Completes OAuth recovery registration for an existing identity: routes the OAuth refresh token
 * into the default space and writes the recovery binding.
 */
export type CompleteOAuthRegistrationRequest = {
  registrationToken: string;
  identityKey: string;
  spaceKey: string;
};

export type CompleteOAuthRegistrationResponse = {
  email?: string;
  provider: OAuthProvider;
  accessTokenId: string;
  accessToken: string;
  expiresInSeconds: number;
  scopes: string[];
  identifier: string;
};

export enum EdgeWebsocketProtocol {
  V0 = 'edge-ws-v0',
  /**
   * Enables message framing and muxing by service-id.
   */
  V1 = 'edge-ws-v1',
  /**
   * Adds per-channel credit-based flow control on top of V1 framing.
   *
   * Must be negotiated rather than sniffed: a V1 reader tests only `FrameFlag.SEGMENT_SEQ`, so it decodes
   * a V2 credit frame as a message body and throws. See docs/design/flow-control/DESIGN.md in
   * dxos/edge.
   */
  V2 = 'edge-ws-v2',
}

/**
 * Per-channel credit window in payload bytes, by service.
 *
 * A window caps one channel at `window / RTT` however fast either end is, so 4 MiB still allows a replicator channel
 * 20 MB/s at a 200 ms round trip, more than one replicator Durable Object absorbs. Swarm and signal traffic is
 * low-volume and latency-sensitive, so a large window would only delay the backpressure signal. Receivers derive
 * their grant threshold from the smallest window and their overdraft bound from the largest, so shrinking one is a
 * protocol change.
 */
export const EDGE_FLOW_CONTROL_WINDOWS = {
  replicator: 4 * 1024 * 1024,
  swarm: 256 * 1024,
  default: 1024 * 1024,
} as const;

/**
 * Messages one channel may have sent but not yet seen consumed.
 *
 * The byte windows alone admit tens of thousands of small subduction frames, and the router dispatches every message
 * downstream on its own (for a replicator, one RPC into its Durable Object), so request count, not bytes, is what
 * overloads the object. Caps one channel at `64 / RTT` messages per second, ~320/s at 200 ms.
 */
export const EDGE_FLOW_CONTROL_MAX_MESSAGES = 64;

/**
 * Messages one connection may have sent but not yet seen consumed, across all its channels.
 *
 * Every message on a device's socket lands in that device's one router Durable Object, and a channel is a service, so
 * one per space: the per-channel bound alone lets a device with 60 spaces hold 3,840 messages in flight there, while a
 * reconnect's burst of ~1,200 feed `get-metadata` frames already overloads it. Feed replies are relayed back through
 * the same object before the request settles, so this also bounds them.
 */
export const EDGE_FLOW_CONTROL_MAX_CONNECTION_MESSAGES = 128;

/** Credit window for the channel carrying `serviceId`, per {@link EDGE_FLOW_CONTROL_WINDOWS}. */
export const edgeFlowControlWindow = (serviceId?: string): number => {
  if (!serviceId) {
    return EDGE_FLOW_CONTROL_WINDOWS.default;
  }
  const [serviceName] = serviceId.split(':');
  switch (serviceName) {
    case EdgeService.SUBDUCTION_REPLICATOR:
    case EdgeService.FEED_REPLICATOR:
    case EdgeService.QUEUE_REPLICATOR:
      return EDGE_FLOW_CONTROL_WINDOWS.replicator;
    case EdgeService.SWARM:
    case EdgeService.SIGNAL:
      return EDGE_FLOW_CONTROL_WINDOWS.swarm;
    default:
      return EDGE_FLOW_CONTROL_WINDOWS.default;
  }
};

/**
 * Prefix of the `Sec-WebSocket-Protocol` entry carrying the client's SDK version (e.g. `dxos-version.0.12.0`).
 * A subprotocol entry because browsers cannot set headers on a WebSocket; the router never selects it.
 */
export const EDGE_CLIENT_VERSION_PROTOCOL_PREFIX = 'dxos-version.';

/** HTTP header carrying the client's SDK version, the {@link EDGE_CLIENT_VERSION_PROTOCOL_PREFIX} of a plain request. */
export const EDGE_CLIENT_VERSION_HEADER = 'X-DXOS-Version';

/** `failure.data.type` of EDGE's 426 refusing an SDK older than the oldest it serves. */
export const EDGE_CLIENT_TOO_OLD = 'client_too_old';

// TODO(mykola): Reconcile with type in EDGE repo.
export type EdgeStatus = {
  problems: string[];
  agent: {
    agentStatus?: string;
    agentKey?: string;
    haloSpaceId?: SpaceId;
    fetchError?: string;
  };
  router: {
    connectedDevices?: {
      peerKey: string;
      topics: string[];
    }[];
    metrics?: {
      sentMessages: number;
      receivedMessages: number;
      sentBytes: number;
      receivedBytes: number;
      failedMessages: number;
      failedBytes: number;
    };
    fetchError?: string;
  };
  spaces: {
    data: Record<SpaceId, { diagnostics?: any & { redFlags: string[] }; fetchError?: string }>;
    fetchError?: string;
  };
};

/** Heads of every Automerge document in a space as last indexed by the EDGE indexer. */
export type IndexerHeadsResponse = {
  spaceId: SpaceId;
  indexingInProgress: boolean;
  documents: { documentId: string; heads: string[] }[];
};

const MAX_ERROR_DEPTH = 3;

/**
 * Codec for serializing and deserializing Edge Errors.
 */
export const ErrorCodec = Object.freeze({
  encode: (err: Error, depth: number = 0): SerializedError => ({
    name: 'name' in err ? err.name : (err as any).code || 'Error',
    message: err.message,
    stack: err.stack,
    cause: err.cause instanceof Error && depth < MAX_ERROR_DEPTH ? ErrorCodec.encode(err.cause, depth + 1) : undefined,
  }),
  decode: (serializedError: SerializedError, depth: number = 0): Error => {
    let err: Error;
    if (typeof serializedError.name === 'string') {
      err = new BaseError(serializedError.name, {
        message: serializedError.message ?? 'Unknown error',
        cause:
          serializedError.cause && depth < MAX_ERROR_DEPTH
            ? ErrorCodec.decode(serializedError.cause, depth + 1)
            : undefined,
        context: serializedError.context,
      });

      if (serializedError.stack) {
        Object.defineProperty(err, 'stack', {
          value: serializedError.stack,
        });
      }
    } else {
      err = new Error(serializedError.message ?? 'Unknown error', {
        cause:
          serializedError.cause && depth < MAX_ERROR_DEPTH
            ? ErrorCodec.decode(serializedError.cause, depth + 1)
            : undefined,
      });

      if (serializedError.stack) {
        Object.defineProperty(err, 'stack', {
          value: serializedError.stack,
        });
      }
    }

    return err;
  },
});

const decodeHttpError = (response: Response, text: string): Error | undefined => {
  if (response.headers.get('Content-Type') !== 'application/json') {
    return new Error(text.slice(0, 256));
  }

  const body = JSON.parse(text);
  if (!('error' in body)) {
    return undefined;
  }

  return ErrorCodec.decode(body.error);
};

/**
 * Codec for serializing and deserializing Edge unhandled HTTP errors.
 */
export const EdgeHttpErrorCodec = Object.freeze({
  encode: (err: Error): Response =>
    new Response(
      JSON.stringify({
        error: ErrorCodec.encode(err),
      }),
      { status: 500 },
    ),

  /** Reads the response body. */
  decode: async (response: Response): Promise<Error | undefined> => decodeHttpError(response, await response.text()),

  /** Decodes a body already read from `response`. */
  decodeText: (response: Response, text: string): Error | undefined => decodeHttpError(response, text),
});

/** Authentication scheme naming a DXOS verifiable presentation. */
export const VERIFIABLE_PRESENTATION_SCHEME = 'VerifiablePresentation';

/** Marks the credential payload as a base64-encoded protobuf. */
const VERIFIABLE_PRESENTATION_TOKEN_PREFIX = 'pb;base64,';

/**
 * Subprotocol prefix carrying credentials on the WebSocket upgrade, where there is no
 * `Authorization` header to put them in.
 */
export const WS_AUTH_PROTOCOL_PREFIX = 'base64url.bearer.authorization.dxos.org';

/**
 * Codec for the credentials header — the single definition of how an encoded verifiable
 * presentation is framed on the wire, in both places it travels:
 *
 * - `Authorization: VerifiablePresentation pb;base64,<base64>` for HTTP.
 * - `base64url.bearer.authorization.dxos.org.<token>` as a WebSocket subprotocol, which admits a
 *   narrower alphabet: `=` padding is stripped and `/` is substituted with `|`, neither being
 *   legal in a subprotocol token.
 *
 * This lives in protocols because both ends own half of it — the client encodes, the Edge worker
 * decodes — and the two had drifted into separate copies of the same string literals.
 *
 * Decoding is deliberately lenient where the RFCs are: the auth-scheme is matched
 * case-insensitively (RFC 9110 §11.1) and anything unrecognised yields `undefined` rather than
 * throwing, so a caller can fall through to another auth method instead of failing the request.
 */
export const EdgeCredentialsHeaderCodec = Object.freeze({
  /** Encode a presentation as an `Authorization` header value. */
  encode: (presentation: Uint8Array): string =>
    `${VERIFIABLE_PRESENTATION_SCHEME} ${VERIFIABLE_PRESENTATION_TOKEN_PREFIX}${uint8ArrayToBase64(presentation)}`,

  /** Decode an `Authorization` header value, or `undefined` if it carries no presentation. */
  decode: (header: string | null | undefined): Uint8Array | undefined => {
    if (!header) {
      return undefined;
    }
    const separator = header.indexOf(' ');
    if (separator === -1) {
      return undefined;
    }
    if (header.slice(0, separator).toLowerCase() !== VERIFIABLE_PRESENTATION_SCHEME.toLowerCase()) {
      return undefined;
    }
    const token = header.slice(separator + 1);
    if (!token.startsWith(VERIFIABLE_PRESENTATION_TOKEN_PREFIX)) {
      return undefined;
    }
    return base64ToUint8Array(token.slice(VERIFIABLE_PRESENTATION_TOKEN_PREFIX.length));
  },

  /** Encode a presentation as a WebSocket subprotocol token. */
  encodeWebSocketProtocol: (presentation: Uint8Array): string => {
    const token = uint8ArrayToBase64(presentation).replace(/=*$/, '').replaceAll('/', '|');
    return `${WS_AUTH_PROTOCOL_PREFIX}.${token}`;
  },

  /** Decode a WebSocket subprotocol token, or `undefined` if it is not a credentials token. */
  decodeWebSocketProtocol: (protocol: string | null | undefined): Uint8Array | undefined => {
    if (!protocol?.startsWith(`${WS_AUTH_PROTOCOL_PREFIX}.`)) {
      return undefined;
    }
    const token = protocol.slice(WS_AUTH_PROTOCOL_PREFIX.length + 1).replaceAll('|', '/');
    return base64ToUint8Array(token);
  },
});

// `Buffer` is unavailable in the browser bundle, so go through the platform base64 primitives.
const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
};

const base64ToUint8Array = (value: string): Uint8Array | undefined => {
  // `atob` rejects the stripped padding the WebSocket framing produces; restore it first.
  const padded = value.padEnd(value.length + ((4 - (value.length % 4)) % 4), '=');
  // `atob` throws on characters outside the base64 alphabet, which would break the `undefined`
  // contract both decoders document for anything unrecognised.
  let binary: string;
  try {
    binary = atob(padded);
  } catch {
    return undefined;
  }
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

//
// Data management.
//

export type ListSpacesRequest = { limit?: number; cursor?: string; order?: 'asc' | 'desc' };
export type ListSpacesResponse = {
  spaces: SpaceActivityEntry[];
  cursor?: string;
  limit: number;
};

export type ListActiveIdentitiesRequest = { cursor?: string; limit?: number };
export type ListActiveIdentitiesResponse = {
  identities: {
    identityDid: string;
    haloSpaceId: string | null;
    createdAt: string | null;
    agentKey: string | null;
    hasRecovery: boolean;
  }[];
  cursor?: string;
  complete: boolean;
  totalCount: number;
};

export type InspectSpaceRequest = { spaceId: string };
export type InspectSpaceResponse = {
  spaceId: string;
  metadata: { createdAt: string; identityDid?: string; status?: 'active' | 'deleting' } | null;
  members: {
    count: number;
    list: { identityDid: string; role?: string; agentKey?: string }[];
  };
  controlFeeds: {
    replicationProgress: { [feedKey: string]: { replicated: number; processed: number } };
  };
  echo: {
    dataFeeds: {
      count: number;
      totalBlocks: number;
      byNamespace: { namespace: string; feeds: { feedId: string; blockCount: number }[] }[];
    };
    documentCount: number;
    objectCount: number;
    deletedObjectCount: number;
    indexedDocumentCount: number;
    objectsByType: { typeURI: string; count: number }[];
    indexerStatus: {
      indexingInProgress: boolean;
      cursors: { indexName: string; sourceName: string; resourceId: string | null; cursor: string | number }[];
      totalChanges: number;
    };
  };
  usageInLast30Days: {
    lastActivity: string | null;
    wsEvents: number;
    httpEvents: number;
    totalEvents: number;
  } | null;
  durableObjects: { type: string; doId: string }[];
};

export type InspectIdentityRequest = { identityDid: string };
export type InspectIdentityResponse = {
  identityDid: string;
  agentKey: string | null;
  haloSpaceId: string | null;
  hasRecovery: boolean;
  routerDoId: string;
  agentDoId: string | null;
  ownedFunctions: { id: string; name: string; versionCount: number }[];
  spaces: InspectSpaceResponse[];
};

/** Matches the SerializedSpace format from @dxos/echo-client, extended with spaceId. */
export type SpaceExportPayload = {
  version: number;
  timestamp: string;
  spaceId: string;
  objects: Record<string, unknown>[];
};

export type SpaceActivityEntry = {
  spaceId: string;
  lastActivity: string;
  totalEvents: number;
  metadata: { createdAt: string; identityDid?: string; status?: 'active' | 'deleting' } | null;
};

export type SpaceExportResult = {
  spaceId: string;
  downloadPath: string;
  downloadUrl: string;
  expiresAt: string;
  objectCount: number;
  sizeBytes: number;
};

export type ExportSpaceRequest = { spaceId: string; origin: string };

export type DeleteSpaceRequest = { spaceId: string };
export type DeleteSpaceResponse = { status: string; spaceId: string };

export type DeleteIdentityRequest = { identityDid: string };
export type DeleteIdentityResponse = { status: string; identityDid: string };

//
// Account / Invitation
//

export const INVITATION_CODE_LENGTH = 8;
export const DEFAULT_INVITATIONS_PER_ACCOUNT = 5;

/** Crockford base32 alphabet (no I, L, O, U). Case-insensitive on the wire. */
export const INVITATION_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * A vanity code is an admin-chosen prefix, a dash, and random characters from
 * {@link INVITATION_CODE_ALPHABET}: `SF-MEETUP-7K2Q`. Hub-service matches codes ignoring case and
 * dashes, so `sfmeetup7k2q` redeems it too.
 */
export const VANITY_PREFIX_MIN_LENGTH = 4;
export const VANITY_PREFIX_MAX_LENGTH = 20;
export const VANITY_SUFFIX_LENGTH = 4;
export const VANITY_CODE_MAX_LENGTH = VANITY_PREFIX_MAX_LENGTH + 1 + VANITY_SUFFIX_LENGTH;
/** 4-20 characters, at least 4 of them letters or digits, with single dashes between them. */
export const VANITY_PREFIX_PATTERN = new RegExp(
  `^(?=.{${VANITY_PREFIX_MIN_LENGTH},${VANITY_PREFIX_MAX_LENGTH}}$)(?=(?:-?[A-Za-z0-9]){${VANITY_PREFIX_MIN_LENGTH}})[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$`,
  'u',
);

export const MAX_CODES_PER_REQUEST = 1000;
export const MAX_REDEMPTIONS_PER_CODE = 100;

/**
 * A code as a user types it: generated or vanity, any case, optionally hyphenated. Hub-service
 * normalizes before lookup. Every issued code has at least {@link INVITATION_CODE_LENGTH} characters.
 */
export const InvitationCodeSchema = Schema.String.pipe(
  Schema.check(
    Schema.isPattern(new RegExp(`^[A-Za-z0-9-]{${INVITATION_CODE_LENGTH},${2 * VANITY_CODE_MAX_LENGTH}}$`, 'u')),
  ),
);

export const CheckEmailExistsRequestSchema = Schema.Struct({
  email: Schema.String,
});
export type CheckEmailExistsRequest = Schema.Schema.Type<typeof CheckEmailExistsRequestSchema>;
export type CheckEmailExistsResponse = { exists: boolean };

export const ValidateInvitationCodeRequestSchema = Schema.Struct({
  code: InvitationCodeSchema,
});
export type ValidateInvitationCodeRequest = Schema.Schema.Type<typeof ValidateInvitationCodeRequestSchema>;
export type ValidateInvitationCodeResponse = { valid: boolean };

/**
 * Body of `POST /account/login`. Existing-account email recovery only -- account creation redeems
 * an invitation code via `/account/invitation-code/redeem` instead, so this never creates
 * identities or waitlist rows.
 */
export const LoginRequestSchema = Schema.Struct({
  email: Schema.String,
  identityDid: Schema.optional(Schema.String),
  identityKey: Schema.optional(Schema.String),
  /**
   * Where the emailed link hands the token back, defaulting to the hub's configured app URL. The
   * hub honors only origins it owns, so a CLI can name its local callback server here.
   */
  redirectUrl: Schema.optional(Schema.String),
});
export type LoginRequest = Schema.Schema.Type<typeof LoginRequestSchema>;

/**
 * Response from `POST /account/login`. The shape is identical regardless of
 * whether the email is registered, so the endpoint is safe against enumeration.
 * Regular emails are delivered out-of-band and the response is `{}`; a recovery
 * token is never returned inline. Test emails in dev-like environments
 * short-circuit with `needsIdentity` / `admitted` before any token exists.
 */
export const LoginResponseSchema = Schema.Struct({
  needsIdentity: Schema.optional(Schema.Boolean),
  admitted: Schema.optional(Schema.Boolean),
});
export type LoginResponse = Schema.Schema.Type<typeof LoginResponseSchema>;

// Two-step signup: invitation code + identity DID + email.
export const RedeemInvitationCodeRequestSchema = Schema.Struct({
  code: Schema.optional(InvitationCodeSchema),
  identityDid: Schema.optional(Schema.String),
  /** Raw hex public key, stored alongside the DID for the magic-link recovery flow. */
  identityKey: Schema.optional(Schema.String),
  email: Schema.String,
});
export type RedeemInvitationCodeRequest = Schema.Schema.Type<typeof RedeemInvitationCodeRequestSchema>;
export type RedeemInvitationCodeResponse =
  | { accountId: string; emailVerificationSent: boolean }
  | { needsIdentity: true };

export const GetAccountResponseSchema = Schema.Struct({
  identityDid: Schema.String,
  email: Schema.String,
  emailVerified: Schema.Boolean,
  /** ISO timestamp. */
  createdAt: Schema.String,
  invitationsRemaining: Schema.Number,
});
export type GetAccountResponse = Schema.Schema.Type<typeof GetAccountResponseSchema>;

export const AccountInvitationSchema = Schema.Struct({
  code: Schema.String,
  /** ISO timestamp. */
  createdAt: Schema.String,
  redeemedByIdentityDid: Schema.optional(Schema.String),
  /** ISO timestamp. */
  redeemedAt: Schema.optional(Schema.String),
});
export type AccountInvitation = Schema.Schema.Type<typeof AccountInvitationSchema>;

export const ListAccountInvitationsResponseSchema = Schema.Struct({
  invitations: Schema.Array(AccountInvitationSchema),
});
export type ListAccountInvitationsResponse = Schema.Schema.Type<typeof ListAccountInvitationsResponseSchema>;

export type IssueInvitationResponse = { code: string };

export type ResendVerificationEmailResponse = {
  sent: boolean;
  cooldownSecondsRemaining?: number;
};

/**
 * Submitted by users without an Account who want to request access. Captured
 * by Hub for admin follow-up (e.g. mailing list, Discord notification).
 */
export const RequestAccessRequestSchema = Schema.Struct({
  email: Schema.String,
  /** Optional: identity DID the user is currently signed in as. */
  identityDid: Schema.optional(Schema.String),
  /** Optional free-form message from the requester. */
  message: Schema.optional(Schema.String),
});
export type RequestAccessRequest = Schema.Schema.Type<typeof RequestAccessRequestSchema>;
export type RequestAccessResponse = { received: boolean };

//
// Metering (VP auth)
//

/** Structured usage key aligned with {@link MeteringLimitSchema} (without cap/window fields). */
export const MeteringUsageKeySchema = Schema.Struct({
  /** Event type (e.g. `ai`). */
  eventType: Schema.String,
  /** Value key being metered (e.g. `outputTokens`). */
  valueKey: Schema.String,
  /**
   * Positional match against the event's subtype segments (e.g. model); `*` matches any
   * segment, and positions beyond the pattern are unconstrained.
   */
  subtypePattern: Schema.Array(Schema.String),
});
export type MeteringUsageKey = Schema.Schema.Type<typeof MeteringUsageKeySchema>;

/** Rolling-window usage total for a structured key. */
export const MeteringUsageItemSchema = Schema.Struct({
  ...MeteringUsageKeySchema.fields,
  amount: Schema.Number,
});
export type MeteringUsageItem = Schema.Schema.Type<typeof MeteringUsageItemSchema>;

/** A single raw usage bucket (1h resolution) for a structured key. */
export const MeteringUsageBucketSchema = Schema.Struct({
  ...MeteringUsageKeySchema.fields,
  /** Bucket start timestamp (epoch ms, floored to the hour). */
  bucketStart: Schema.Number,
  amount: Schema.Number,
});
export type MeteringUsageBucket = Schema.Schema.Type<typeof MeteringUsageBucketSchema>;

export const MeteringLimitSchema = Schema.Struct({
  /** Event type the limit applies to (e.g. `ai`). */
  eventType: Schema.String,
  /** Value key being limited (e.g. `outputTokens`). */
  valueKey: Schema.String,
  /**
   * Positional match against the event's subtype segments (e.g. model); `*` matches any
   * segment, and positions beyond the pattern are unconstrained.
   */
  subtypePattern: Schema.Array(Schema.String),
  /** Rolling-window cap; `null` means unlimited. */
  limit: Schema.NullOr(Schema.Number),
  /** Window duration in seconds. */
  windowDuration: Schema.Number,
});
export type MeteringLimit = Schema.Schema.Type<typeof MeteringLimitSchema>;

export const GetProfileUsageResponseSchema = Schema.Struct({
  profileId: Schema.String,
  usage: Schema.Array(MeteringUsageItemSchema),
  limits: Schema.Array(MeteringLimitSchema),
  buckets: Schema.Array(MeteringUsageBucketSchema),
});
export type GetProfileUsageResponse = Schema.Schema.Type<typeof GetProfileUsageResponseSchema>;

//
// Admin (X-API-KEY)
//

export type AdminListAccountsResponse = {
  accounts: GetAccountResponse[];
};

export const AdminGrantInvitationsRequestSchema = Schema.Struct({
  identityDid: Schema.String,
  count: Schema.Number,
});
export type AdminGrantInvitationsRequest = Schema.Schema.Type<typeof AdminGrantInvitationsRequestSchema>;

export const AdminCreateInvitationCodesRequestSchema = Schema.Struct({
  count: Schema.Number.check(Schema.isInt(), Schema.isBetween({ minimum: 1, maximum: MAX_CODES_PER_REQUEST })),
  note: Schema.optional(Schema.String),
  /** Plan the redeemer inherits, overriding the default user plan. Single-use codes only. */
  planName: Schema.optional(Schema.String),
  /** How many accounts may sign up with each code (default 1), e.g. for an event code. */
  maxRedemptions: Schema.optional(
    Schema.Number.check(Schema.isInt(), Schema.isBetween({ minimum: 1, maximum: MAX_REDEMPTIONS_PER_CODE })),
  ),
  /**
   * Vanity prefix (requires `count: 1`). The code is the uppercased prefix, a dash, and
   * {@link VANITY_SUFFIX_LENGTH} random characters, so `sf-meetup` yields e.g. `SF-MEETUP-7K2Q`.
   */
  prefix: Schema.optional(Schema.String.check(Schema.isPattern(VANITY_PREFIX_PATTERN))),
}).check(
  Schema.makeFilter((request) => {
    const issues: Schema.FilterIssue[] = [];
    if (request.prefix !== undefined && request.count !== 1) {
      issues.push({ path: ['count'], issue: 'A vanity code is created one at a time; count must be 1.' });
    }
    if (request.planName && (request.maxRedemptions ?? 1) > 1) {
      issues.push({ path: ['planName'], issue: 'A code that carries a plan must be single-use.' });
    }
    return issues;
  }),
);
export type AdminCreateInvitationCodesRequest = Schema.Schema.Type<typeof AdminCreateInvitationCodesRequestSchema>;
export type AdminCreateInvitationCodesResponse = { codes: string[] };

export type AdminListInvitationCodesResponse = {
  codes: Array<{
    code: string;
    /** ISO timestamp. */
    createdAt: string;
    note?: string;
    issuedByIdentityDid?: string;
    /** Set for single-use codes only; see `redemptionCount` for the rest. */
    redeemedByIdentityDid?: string;
    /** ISO timestamp of the most recent redemption. */
    redeemedAt?: string;
    /** ISO timestamp. Set when revoked. */
    revokedAt?: string;
    /** Absent from hubs that predate code capacity; treat as 1. */
    maxRedemptions?: number;
    /** Absent from hubs that predate code capacity. */
    redemptionCount?: number;
  }>;
};

export const AdminRevokeInvitationCodeRequestSchema = Schema.Struct({
  code: InvitationCodeSchema,
});
export type AdminRevokeInvitationCodeRequest = Schema.Schema.Type<typeof AdminRevokeInvitationCodeRequestSchema>;

/**
 * Account/invitation-related variants placed in `EdgeFailure.data.type`.
 * EdgeErrorData is open-ended; these are documentation for known values.
 */
export const ACCOUNT_ERROR_TYPES = [
  'invitation_code_invalid',
  'invitation_code_already_redeemed',
  'invitation_code_revoked',
  'email_already_registered',
  'identity_already_associated',
  'no_invitations_remaining',
  'identity_not_associated_with_account',
  'no_account',
  'rate_limited',
] as const;
export type AccountErrorType = (typeof ACCOUNT_ERROR_TYPES)[number];
